/**
 * Raiz da área autenticada da equipe (spec 017 D3 + spec 018): escolhe o shell
 * pela preferência da pessoa — Completo (congelado) ou Simplificado. O
 * redirecionamento principal acontece no `beforeLoad` da rota (antes de
 * renderizar); este efeito cobre o que acontece COM a tela aberta: a troca pelo
 * menu do usuário e um link interno de uma tela reaproveitada que aponte para o
 * equivalente do outro layout.
 */
import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { resolveLayout, type UiLayout } from '@erp/shared';
import { AppLayout } from '@/shared/app-layout';
import { SimpleShell } from '@/shared/simple-shell';
import { useUiPrefs } from '@/shared/ui-prefs';
import { profileQueryOptions } from '@/features/account/account-queries';
import { NewsTourHost } from '@/features/novidades/news-tour';
import { layoutRedirect } from './simple-nav';

export function LayoutRoot() {
  const ui = useUiPrefs();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search });

  useEffect(() => {
    if (!ui.ready) return;
    const target = layoutRedirect(ui.layout, pathname, search as Record<string, unknown>);
    if (!target) return;
    void navigate({ to: target.to, params: target.params, search: target.search, replace: true } as never);
  }, [ui.ready, ui.layout, pathname, search, navigate]);

  return (
    <>
      {ui.layout === 'simplified' ? <SimpleShell /> : <AppLayout />}
      {/* o tour das Novidades (spec 036) sobrevive à troca de tela: mora acima do shell */}
      <NewsTourHost audience="staff" />
    </>
  );
}

/**
 * Layout de quem está entrando, para o `beforeLoad` decidir o redirecionamento
 * ANTES de montar a tela errada. O /me já é buscado de qualquer jeito (marca,
 * permissões); aqui ele só chega mais cedo. Falha no /me = Completo — a tela
 * de sempre, nunca uma tela branca.
 */
export async function layoutForRouting(query: QueryClient): Promise<UiLayout> {
  try {
    const profile = await query.ensureQueryData(profileQueryOptions());
    return resolveLayout(profile.uiPrefs, profile.defaultLayout ?? 'classic');
  } catch {
    return 'classic';
  }
}
